import React, { useState, useEffect } from 'react';
import { 
  Building2, 
  TrendingUp, 
  AlertTriangle, 
  Search, 
  RefreshCw,
  Sparkles,
  PieChart,
  BarChart3,
  Shield,
  Flame,
  Zap,
  CheckCircle,
  XCircle,
  Clock,
  ThumbsUp,
  ThumbsDown,
  ChevronDown,
  Loader2,
  BookOpen,
  DollarSign,
  Database,
  Cpu,
  Brain,
  TrendingDown
} from 'lucide-react';
import { api } from '../../utils/api';
import { toast } from 'sonner';

interface VendorSummary {
  total_transactions: number;
  vendors_detected: number;
  top_vendors: { name: string; count: number }[];
  category_distribution: Record<string, number>;
  disaster_vendors: string[];
  unknown_vendors_count: number;
  high_confidence_rate: number;
}

interface KnownVendors {
  total_known_vendors: number;
  base_vendors?: {
    disaster_restoration: number;
    cybersecurity: number;
    business: number;
  };
  expanded_vendors?: {
    [key: string]: number;
  };
  vendors: {
    disaster_restoration: any[];
    cybersecurity: any[];
    business: any[];
  };
}

interface ExtractionResult {
  vendor_name: string;
  confidence: number;
  source: string;
  is_known_vendor: boolean;
  vendor_category?: string;
  reasoning?: string;
}

interface PendingVendor {
  vendor_name: string;
  normalized_name: string;
  occurrences: number;
  status: string;
  last_seen: string;
  sample_descriptions?: string[];
}

interface LearnedVendor {
  vendor_name: string;
  category: string;
  subcategory?: string;
  approved_by: string;
  approved_at: string;
}

interface VendorStats {
  pending_review: number;
  approved_vendors: number;
  rejected_vendors: number;
  total_discovered?: number;
}

interface TierBreakdown {
  count: number;
  percentage: number;
  description: string;
  cost: number;
}

interface ClassificationTierStats {
  tier_breakdown: {
    tier_1_local_db: TierBreakdown;
    tier_2_local_ml: TierBreakdown;
    tier_3_dual_ai: TierBreakdown;
    human_review?: { count: number; description: string };
  };
  cost_savings: {
    api_calls_avoided: number;
    cost_per_api_call: number;
    total_saved: number;
    total_incurred: number;
    savings_rate: number;
  };
  database_stats: {
    unique_vendors: number;
    categories: number;
    databases_merged?: number;
  };
  total_transactions: number;
  message: string;
}

// Category options for vendor approval
const CATEGORY_OPTIONS = [
  { value: 'Disaster Recovery', subcategories: ['Fire Restoration', 'Flood Remediation', 'Water Extraction', 'Smoke Damage', 'Mold Remediation', 'General Restoration'] },
  { value: 'Temporary Operations', subcategories: ['Temporary Location', 'Equipment Rental', 'Temporary Staffing'] },
  { value: 'Business Continuity', subcategories: ['Data Recovery', 'IT Restoration', 'Communication Setup'] },
  { value: 'Payroll & Benefits', subcategories: ['Payroll Processing', 'Benefits Administration', 'HR Services'] },
  { value: 'Technology & Software', subcategories: ['SaaS', 'Cloud Services', 'Security Software', 'Productivity Tools'] },
  { value: 'Utilities', subcategories: ['Electric', 'Gas', 'Water', 'Telecom', 'Internet'] },
  { value: 'Professional Services', subcategories: ['Accounting', 'Legal', 'Consulting', 'Marketing'] },
  { value: 'Insurance', subcategories: ['Property', 'Liability', 'Workers Comp', 'Cyber'] },
  { value: 'Food & Beverage Cost', subcategories: ['Food Distribution', 'Beverage', 'Supplies'] },
  { value: 'Shipping & Logistics', subcategories: ['Shipping', 'Freight', 'Delivery'] },
  { value: 'Equipment & Supplies', subcategories: ['Office Supplies', 'IT Equipment', 'Industrial Equipment'] },
];

export default function VendorIntelligenceDashboard() {
  const [summary, setSummary] = useState<VendorSummary | null>(null);
  const [knownVendors, setKnownVendors] = useState<KnownVendors | null>(null);
  const [loading, setLoading] = useState(true);
  const [testDescription, setTestDescription] = useState('');
  const [testResult, setTestResult] = useState<ExtractionResult | null>(null);
  const [testing, setTesting] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'known' | 'pending' | 'learned' | 'test'>('overview');
  
  // Pending vendors state
  const [pendingVendors, setPendingVendors] = useState<PendingVendor[]>([]);
  const [vendorStats, setVendorStats] = useState<VendorStats | null>(null);
  const [learnedVendors, setLearnedVendors] = useState<LearnedVendor[]>([]);
  const [approvalLoading, setApprovalLoading] = useState<string | null>(null);
  const [tierStats, setTierStats] = useState<ClassificationTierStats | null>(null);
  
  // Approval form state
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedSubcategory, setSelectedSubcategory] = useState<string>('');
  const [expandedVendor, setExpandedVendor] = useState<string | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch all data in parallel with individual error handling
      const results = await Promise.allSettled([
        api.get('/api/v1/vendor-intelligence/summary'),
        api.get('/api/v1/vendor-intelligence/known-vendors'),
        api.get('/api/v1/vendor-intelligence/discovered'),
        api.get('/api/v1/vendor-intelligence/learned'),
        api.get('/api/v1/vendor-intelligence/classification-tiers')
      ]);
      
      // Process results individually, using defaults for failures
      if (results[0].status === 'fulfilled') {
        setSummary(results[0].value);
      } else {
        console.error('Summary fetch failed:', results[0].reason);
        setSummary({
          total_transactions: 0,
          vendors_detected: 0,
          top_vendors: [],
          category_distribution: {},
          disaster_vendors: [],
          unknown_vendors_count: 0,
          high_confidence_rate: 0
        });
      }
      
      if (results[1].status === 'fulfilled') {
        setKnownVendors(results[1].value);
      } else {
        console.error('Known vendors fetch failed:', results[1].reason);
        setKnownVendors({
          total_known_vendors: 397,
          vendors: { disaster_restoration: [], cybersecurity: [], business: [] }
        });
      }
      
      if (results[2].status === 'fulfilled') {
        setPendingVendors(results[2].value.pending_vendors || []);
        setVendorStats(results[2].value.stats || null);
      } else {
        console.error('Discovered vendors fetch failed:', results[2].reason);
        setPendingVendors([]);
        setVendorStats(null);
      }
      
      if (results[3].status === 'fulfilled') {
        setLearnedVendors(results[3].value.learned_vendors || []);
      } else {
        console.error('Learned vendors fetch failed:', results[3].reason);
        setLearnedVendors([]);
      }

      if (results[4].status === 'fulfilled') {
        setTierStats(results[4].value);
      } else {
        console.error('Tier stats fetch failed:', results[4].reason);
        setTierStats(null);
      }
    } catch (error) {
      console.error('Error fetching vendor data:', error);
      toast.error('Failed to load vendor data');
    } finally {
      setLoading(false);
    }
  };

  const approveVendor = async (vendorName: string) => {
    if (!selectedCategory) {
      toast.error('Please select a category');
      return;
    }
    
    setApprovalLoading(vendorName);
    try {
      await api.post('/api/v1/vendor-intelligence/discovered/approve', {
        vendor_name: vendorName,
        category: selectedCategory,
        subcategory: selectedSubcategory,
        confidence: 0.85
      });
      toast.success(`Approved: ${vendorName}`);
      setExpandedVendor(null);
      setSelectedCategory('');
      setSelectedSubcategory('');
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Error approving vendor:', error);
      toast.error('Failed to approve vendor');
    } finally {
      setApprovalLoading(null);
    }
  };

  const rejectVendor = async (vendorName: string) => {
    setApprovalLoading(vendorName);
    try {
      await api.post('/api/v1/vendor-intelligence/discovered/reject', {
        vendor_name: vendorName
      });
      toast.success(`Rejected: ${vendorName}`);
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Error rejecting vendor:', error);
      toast.error('Failed to reject vendor');
    } finally {
      setApprovalLoading(null);
    }
  };

  const testExtraction = async () => {
    if (!testDescription.trim()) return;
    
    setTesting(true);
    try {
      const result = await api.post('/api/v1/vendor-intelligence/extract', {
        description: testDescription
      });
      setTestResult(result);
    } catch (error) {
      console.error('Error testing extraction:', error);
    } finally {
      setTesting(false);
    }
  };

  const getCategoryColor = (category: string): React.CSSProperties => {
    const colors: Record<string, React.CSSProperties> = {
      'Disaster Recovery': { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' },
      'Temporary Operations': { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' },
      'Business Continuity': { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' },
      'Payroll & Benefits': { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' },
      'Technology & Software': { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' },
      'Utilities': { backgroundColor: 'transparent', color: 'var(--color-verified)' },
      'Food & Beverage Cost': { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' },
      'Insurance': { backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' },
    };
    return colors[category] || { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin" style={{ color: 'var(--color-needs-human-text)' }} />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
            <Building2 className="w-7 h-7" style={{ color: 'var(--color-needs-human-text)' }} />
            Vendor Intelligence Dashboard
          </h1>
          <p className="mt-1" style={{ color: 'var(--color-text-muted)' }}>
            AI-powered vendor detection and classification analytics
          </p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 rounded-lg transition-colors"
          style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b" style={{ borderColor: 'var(--color-border)' }}>
        {[
          { id: 'overview', label: 'Overview', icon: PieChart },
          { id: 'pending', label: `Pending Review (${pendingVendors.length})`, icon: Clock, highlight: pendingVendors.length > 0 },
          { id: 'learned', label: `Learned (${learnedVendors.length})`, icon: BookOpen },
          { id: 'known', label: 'Known Vendors', icon: Building2 },
          { id: 'test', label: 'Test Extraction', icon: Search },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors border-b-2 -mb-px ${tab.highlight ? 'relative' : ''}`}
            style={
              activeTab === tab.id
                ? { color: 'var(--color-needs-human-text)', borderColor: 'var(--color-brand)' }
                : { color: 'var(--color-text-muted)', borderColor: 'transparent' }
            }
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.highlight && (
              <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: 'var(--color-brand)' }}></span>
            )}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && summary && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl p-5 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Total Transactions</p>
                  <p className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{Number(summary.total_transactions ?? 0).toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                  <BarChart3 className="w-6 h-6" style={{ color: 'var(--color-needs-human-text)' }} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Vendors Detected</p>
                  <p className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{Number(summary.vendors_detected ?? 0).toLocaleString()}</p>
                </div>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'transparent' }}>
                  <Building2 className="w-6 h-6" style={{ color: 'var(--color-verified)' }} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>High Confidence Rate</p>
                  <p className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{Number(summary.high_confidence_rate ?? 0)}%</p>
                </div>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'transparent' }}>
                  <Sparkles className="w-6 h-6" style={{ color: 'var(--color-verified)' }} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-5 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Unknown Vendors</p>
                  <p className="text-2xl font-bold" style={{ color: 'var(--color-brand)' }}>{Number(summary.unknown_vendors_count ?? 0)}</p>
                </div>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                  <AlertTriangle className="w-6 h-6" style={{ color: 'var(--color-brand)' }} />
                </div>
              </div>
            </div>
          </div>

          {/* Classification Tiers & Cost Savings - NEW SECTION
              Audit 2026-04-25 (F2.75/F2.76): when there are zero classified
              transactions, the backend returns its expected-distribution
              defaults (85/10/5) and a divide-by-zero "95%" savings rate
              that look like real data. Hide the bars + savings card entirely
              until at least one transaction has been classified. */}
          {tierStats && (
            (tierStats.tier_breakdown?.tier_1_local_db?.count || 0) +
              (tierStats.tier_breakdown?.tier_2_local_ml?.count || 0) +
              (tierStats.tier_breakdown?.tier_3_dual_ai?.count || 0) === 0
          ) && (
            <div className="rounded-xl p-6 text-center border" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)', color: 'var(--color-text-muted)' }}>
              <Database className="w-6 h-6 mx-auto mb-2" style={{ color: 'var(--color-text-dim)' }} />
              <p className="text-sm font-medium">No classified transactions yet</p>
              <p className="text-xs mt-1">Tier breakdown and cost savings will appear once vendors are classified.</p>
            </div>
          )}
          {tierStats && (
            (tierStats.tier_breakdown?.tier_1_local_db?.count || 0) +
              (tierStats.tier_breakdown?.tier_2_local_ml?.count || 0) +
              (tierStats.tier_breakdown?.tier_3_dual_ai?.count || 0) > 0
          ) && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Tier Breakdown */}
              <div className="lg:col-span-2 rounded-xl p-6 shadow-lg" style={{ backgroundColor: 'var(--color-bg-surface)', color: 'var(--color-text-primary)' }}>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Database className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
                  Classification Tier Breakdown
                </h3>
                <p className="text-sm mb-4" style={{ color: 'var(--color-text-muted)' }}>
                  How transactions are being classified - local database saves API costs
                </p>
                
                {/* Tier Progress Bars */}
                <div className="space-y-4">
                  {/* Tier 1 - Local DB */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Database className="w-4 h-4" style={{ color: 'var(--color-verified)' }} />
                        <span className="text-sm font-medium">Tier 1: Local Database</span>
                        <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'transparent', color: 'var(--color-verified)' }}>FREE</span>
                      </div>
                      <span className="font-bold" style={{ color: 'var(--color-verified)' }}>{(tierStats.tier_breakdown?.tier_1_local_db?.percentage ?? 0)}%</span>
                    </div>
                    <div className="h-3 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--chart-track)' }}>
                      <div
                        className="h-full rounded-full transition-all duration-1000"
                        style={{ width: `${(tierStats.tier_breakdown?.tier_1_local_db?.percentage ?? 0)}%`, backgroundColor: 'var(--chart-positive)' }}
                      />
                    </div>
                    <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                      {Number(tierStats.tier_breakdown?.tier_1_local_db?.count ?? 0).toLocaleString()} transactions • {Number(tierStats.database_stats?.unique_vendors ?? 0).toLocaleString()} vendors in database
                    </p>
                  </div>

                  {/* Tier 2 - Local ML */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Cpu className="w-4 h-4" style={{ color: 'var(--chart-1)' }} />
                        <span className="text-sm font-medium">Tier 2: HuggingFace ML</span>
                        <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--chart-1)' }}>FREE</span>
                      </div>
                      <span className="font-bold" style={{ color: 'var(--chart-1)' }}>{(tierStats.tier_breakdown?.tier_2_local_ml?.percentage ?? 0)}%</span>
                    </div>
                    <div className="h-3 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--chart-track)' }}>
                      <div
                        className="h-full rounded-full transition-all duration-1000"
                        style={{ width: `${(tierStats.tier_breakdown?.tier_2_local_ml?.percentage ?? 0)}%`, backgroundColor: 'var(--chart-1)' }}
                      />
                    </div>
                    <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                      {Number(tierStats.tier_breakdown?.tier_2_local_ml?.count ?? 0).toLocaleString()} transactions • Zero-shot classification
                    </p>
                  </div>

                  {/* Tier 3 - Dual AI */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Brain className="w-4 h-4" style={{ color: 'var(--chart-2)' }} />
                        <span className="text-sm font-medium">Tier 3: Claude + GPT</span>
                        <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--chart-2)' }}>PAID</span>
                      </div>
                      <span className="font-bold" style={{ color: 'var(--chart-2)' }}>{(tierStats.tier_breakdown?.tier_3_dual_ai?.percentage ?? 0)}%</span>
                    </div>
                    <div className="h-3 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--chart-track)' }}>
                      <div
                        className="h-full rounded-full transition-all duration-1000"
                        style={{ width: `${(tierStats.tier_breakdown?.tier_3_dual_ai?.percentage ?? 0)}%`, backgroundColor: 'var(--chart-2)' }}
                      />
                    </div>
                    <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                      {Number(tierStats.tier_breakdown?.tier_3_dual_ai?.count ?? 0).toLocaleString()} transactions • ${Number(tierStats.tier_breakdown?.tier_3_dual_ai?.cost ?? 0).toFixed(2)} API cost
                    </p>
                  </div>
                </div>
              </div>

              {/* Cost Savings Card */}
              <div className="rounded-xl p-6 shadow-lg" style={{ backgroundColor: 'transparent', color: 'var(--color-text-primary)' }}>
                <h3 className="text-lg font-semibold mb-2 flex items-center gap-2" style={{ color: 'var(--color-verified)' }}>
                  <TrendingDown className="w-5 h-5" />
                  API Cost Savings
                </h3>
                <p className="text-sm mb-4" style={{ color: 'var(--color-text-secondary)' }}>
                  Expanded vendor database reduces expensive API calls
                </p>

                <div className="space-y-4">
                  {/* Savings Rate */}
                  <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
                    <div className="flex items-center justify-between">
                      <span style={{ color: 'var(--color-text-secondary)' }}>Savings Rate</span>
                      <span className="text-3xl font-bold" style={{ color: 'var(--color-verified)' }}>{(tierStats.cost_savings?.savings_rate ?? 0)}%</span>
                    </div>
                    <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
                      of transactions resolved without API calls
                    </p>
                  </div>

                  {/* Money Saved */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-lg p-3 text-center" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
                      <DollarSign className="w-5 h-5 mx-auto mb-1" style={{ color: 'var(--color-verified)' }} />
                      <p className="text-xl font-bold" style={{ color: 'var(--color-verified)' }}>${Number(tierStats.cost_savings?.total_saved ?? 0).toFixed(2)}</p>
                      <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Saved</p>
                    </div>
                    <div className="rounded-lg p-3 text-center" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
                      <p className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{Number(tierStats.cost_savings?.api_calls_avoided ?? 0).toLocaleString()}</p>
                      <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>API calls avoided</p>
                    </div>
                  </div>

                  {/* Database Stats */}
                  <div className="pt-3 border-t" style={{ borderColor: 'var(--color-border-sub)' }}>
                    <div className="flex items-center justify-between text-sm">
                      <span style={{ color: 'var(--color-text-secondary)' }}>Database Size</span>
                      <span className="font-semibold">{Number(tierStats.database_stats?.unique_vendors ?? 0).toLocaleString()} vendors</span>
                    </div>
                    <div className="flex items-center justify-between text-sm mt-1">
                      <span style={{ color: 'var(--color-text-secondary)' }}>Categories</span>
                      <span className="font-semibold">{(tierStats.database_stats?.categories ?? 0)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Top Vendors & Disaster Vendors */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Vendors */}
            <div className="bg-white rounded-xl p-6 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                <TrendingUp className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
                Top Vendors by Frequency
              </h3>
              <div className="space-y-3">
                {(Array.isArray(summary.top_vendors) ? summary.top_vendors : []).slice(0, 10).map((vendor, idx) => (
                  <div key={idx} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-sm w-5" style={{ color: 'var(--color-text-dim)' }}>{idx + 1}.</span>
                      <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{vendor.name}</span>
                    </div>
                    <span className="text-sm px-2 py-1 rounded-full" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                      {vendor.count} txns
                    </span>
                  </div>
                ))}
                {(Array.isArray(summary.top_vendors) ? summary.top_vendors : []).length === 0 && (
                  <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>No vendors detected yet</p>
                )}
              </div>
            </div>

            {/* Disaster Recovery Vendors */}
            <div className="bg-white rounded-xl p-6 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                <Flame className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
                Disaster Recovery Vendors
              </h3>
              <div className="flex flex-wrap gap-2">
                {(Array.isArray(summary.disaster_vendors) ? summary.disaster_vendors : []).map((vendor, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1.5 rounded-lg text-sm font-medium border"
                    style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', borderColor: 'var(--color-needs-human-tint-bd)' }}
                  >
                    {vendor}
                  </span>
                ))}
                {(Array.isArray(summary.disaster_vendors) ? summary.disaster_vendors : []).length === 0 && (
                  <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>No disaster recovery vendors detected</p>
                )}
              </div>
            </div>
          </div>

          {/* Category Distribution */}
          <div className="bg-white rounded-xl p-6 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
              <PieChart className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
              Category Distribution
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(summary.category_distribution ?? {})
                .sort((a, b) => b[1] - a[1])
                .slice(0, 12)
                .map(([category, count]) => (
                  <div
                    key={category}
                    className="p-3 rounded-lg"
                    style={getCategoryColor(category)}
                  >
                    <p className="font-medium text-sm truncate">{category}</p>
                    <p className="text-lg font-bold">{Number(count ?? 0).toLocaleString()}</p>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Known Vendors Tab */}
      {activeTab === 'known' && knownVendors && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                Known Vendor Database
              </h3>
              <span className="text-sm px-3 py-1 rounded-full font-bold" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                {knownVendors.total_known_vendors} total vendors
              </span>
            </div>

            {/* Expanded Database Stats */}
            {knownVendors.expanded_vendors && (
              <div className="mb-6 p-4 rounded-xl border" style={{ backgroundColor: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
                <h4 className="font-medium mb-3 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                  <Sparkles className="w-4 h-4" style={{ color: 'var(--color-needs-human-text)' }} />
                  Expanded Database Breakdown
                </h4>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                  {Object.entries(knownVendors.expanded_vendors)
                    .filter(([key]) => key !== 'total')
                    .sort((a, b) => b[1] - a[1])
                    .map(([category, count]) => (
                      <div key={category} className="text-center p-2 rounded-lg shadow-sm" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
                        <p className="text-lg font-bold" style={{ color: 'var(--color-needs-human-text)' }}>{count}</p>
                        <p className="text-xs capitalize" style={{ color: 'var(--color-text-muted)' }}>{category.replace(/_/g, ' ')}</p>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Disaster Restoration */}
            <div className="mb-6">
              <h4 className="font-medium mb-3 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                <Flame className="w-4 h-4" style={{ color: 'var(--chart-1)' }} />
                Disaster Restoration ({knownVendors.vendors.disaster_restoration?.length || 0})
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {knownVendors.vendors.disaster_restoration?.map((vendor, idx) => (
                  <div key={idx} className="p-2 rounded-lg border" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border-sub)' }}>
                    <p className="font-medium text-sm" style={{ color: 'var(--color-text-primary)' }}>{vendor.name}</p>
                    <p className="text-xs" style={{ color: 'var(--chart-1)' }}>{vendor.subcategory}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Cybersecurity */}
            <div className="mb-6">
              <h4 className="font-medium mb-3 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                <Shield className="w-4 h-4" style={{ color: 'var(--chart-2)' }} />
                Cybersecurity ({knownVendors.vendors.cybersecurity?.length || 0})
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {knownVendors.vendors.cybersecurity?.map((vendor, idx) => (
                  <div key={idx} className="p-2 rounded-lg border" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border-sub)' }}>
                    <p className="font-medium text-sm" style={{ color: 'var(--color-text-primary)' }}>{vendor.name}</p>
                    <p className="text-xs" style={{ color: 'var(--chart-2)' }}>{vendor.subcategory}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Business Vendors */}
            <div>
              <h4 className="font-medium mb-3 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                <Building2 className="w-4 h-4" style={{ color: 'var(--chart-3)' }} />
                Business Vendors (showing 30 of {knownVendors.vendors.business?.length || 0})
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {knownVendors.vendors.business?.slice(0, 30).map((vendor, idx) => (
                  <div key={idx} className="p-2 rounded-lg border" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border-sub)' }}>
                    <p className="font-medium text-xs" style={{ color: 'var(--color-text-primary)' }}>{vendor.name}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pending Review Tab - NEW */}
      {activeTab === 'pending' && (
        <div className="space-y-6">
          {/* Stats Cards */}
          {vendorStats && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl p-5 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Pending Review</p>
                    <p className="text-2xl font-bold" style={{ color: 'var(--color-brand)' }}>{vendorStats.pending_review}</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                    <Clock className="w-6 h-6" style={{ color: 'var(--color-brand)' }} />
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-5 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-verified)' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Approved</p>
                    <p className="text-2xl font-bold" style={{ color: 'var(--color-verified)' }}>{vendorStats.approved_vendors}</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'transparent' }}>
                    <ThumbsUp className="w-6 h-6" style={{ color: 'var(--color-verified)' }} />
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl p-5 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Rejected</p>
                    <p className="text-2xl font-bold" style={{ color: 'var(--color-text-muted)' }}>{vendorStats.rejected_vendors}</p>
                  </div>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                    <ThumbsDown className="w-6 h-6" style={{ color: 'var(--color-text-muted)' }} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Pending Vendors List */}
          <div className="bg-white rounded-xl p-6 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
              <AlertTriangle className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
              Vendors Pending Review
            </h3>
            <p className="text-sm mb-4" style={{ color: 'var(--color-text-muted)' }}>
              These vendors have been detected 3+ times but aren't in the known database. Review and approve to improve future classification accuracy.
            </p>

            {pendingVendors.length === 0 ? (
              <div className="text-center py-8" style={{ color: 'var(--color-text-muted)' }}>
                <CheckCircle className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--color-verified)' }} />
                <p className="font-medium">All caught up!</p>
                <p className="text-sm">No vendors pending review.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pendingVendors.map((vendor) => (
                  <div
                    key={vendor.vendor_name}
                    className="border rounded-lg transition-all"
                    style={
                      expandedVendor === vendor.vendor_name
                        ? { borderColor: 'var(--color-needs-human-tint-bd)', backgroundColor: 'var(--color-needs-human-tint)' }
                        : { borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-surface)' }
                    }
                  >
                    {/* Vendor Header */}
                    <div
                      className="flex items-center justify-between p-4 cursor-pointer"
                      onClick={() => setExpandedVendor(expandedVendor === vendor.vendor_name ? null : vendor.vendor_name)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
                          <Building2 className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
                        </div>
                        <div>
                          <p className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{vendor.vendor_name}</p>
                          <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                            Seen {vendor.occurrences} times • Last: {new Date(vendor.last_seen).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="px-2.5 py-1 text-xs rounded-full font-medium" style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}>
                          {vendor.occurrences} occurrences
                        </span>
                        <ChevronDown className={`w-5 h-5 transition-transform ${
                          expandedVendor === vendor.vendor_name ? 'rotate-180' : ''
                        }`} style={{ color: 'var(--color-text-dim)' }} />
                      </div>
                    </div>

                    {/* Expanded Approval Form */}
                    {expandedVendor === vendor.vendor_name && (
                      <div className="px-4 pb-4 border-t" style={{ borderColor: 'var(--color-needs-human-tint-bd)', backgroundColor: 'var(--color-needs-human-tint)' }}>
                        <div className="pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Category Select */}
                          <div>
                            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Category *</label>
                            <select
                              value={selectedCategory}
                              onChange={(e) => {
                                setSelectedCategory(e.target.value);
                                setSelectedSubcategory('');
                              }}
                              className="w-full px-3 py-2 border rounded-lg"
                              style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-surface)', color: 'var(--color-text-primary)' }}
                            >
                              <option value="">Select category...</option>
                              {CATEGORY_OPTIONS.map(cat => (
                                <option key={cat.value} value={cat.value}>{cat.value}</option>
                              ))}
                            </select>
                          </div>

                          {/* Subcategory Select */}
                          <div>
                            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Subcategory</label>
                            <select
                              value={selectedSubcategory}
                              onChange={(e) => setSelectedSubcategory(e.target.value)}
                              className="w-full px-3 py-2 border rounded-lg"
                              style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-surface)', color: 'var(--color-text-primary)' }}
                              disabled={!selectedCategory}
                            >
                              <option value="">Select subcategory...</option>
                              {CATEGORY_OPTIONS
                                .find(c => c.value === selectedCategory)
                                ?.subcategories.map(sub => (
                                  <option key={sub} value={sub}>{sub}</option>
                                ))}
                            </select>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3 mt-4">
                          <button
                            onClick={() => approveVendor(vendor.vendor_name)}
                            disabled={!selectedCategory || approvalLoading === vendor.vendor_name}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            style={{ backgroundColor: 'var(--color-verified)', color: 'var(--color-on-brand)' }}
                          >
                            {approvalLoading === vendor.vendor_name ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <ThumbsUp className="w-4 h-4" />
                            )}
                            Approve
                          </button>
                          <button
                            onClick={() => rejectVendor(vendor.vendor_name)}
                            disabled={approvalLoading === vendor.vendor_name}
                            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg disabled:opacity-50 transition-colors"
                            style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                          >
                            {approvalLoading === vendor.vendor_name ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <ThumbsDown className="w-4 h-4" />
                            )}
                            Reject
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Learned Vendors Tab - NEW */}
      {activeTab === 'learned' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
              <BookOpen className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
              Admin-Approved Vendors
            </h3>
            <p className="text-sm mb-4" style={{ color: 'var(--color-text-muted)' }}>
              Vendors that have been reviewed and approved by administrators. These are now part of the known vendor database.
            </p>

            {learnedVendors.length === 0 ? (
              <div className="text-center py-8" style={{ color: 'var(--color-text-muted)' }}>
                <Building2 className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--color-text-ghost)' }} />
                <p className="font-medium">No learned vendors yet</p>
                <p className="text-sm">Approve vendors from the Pending Review tab to see them here.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b" style={{ borderColor: 'var(--color-border)' }}>
                      <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>Vendor</th>
                      <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>Category</th>
                      <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>Subcategory</th>
                      <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>Approved By</th>
                      <th className="text-left py-3 px-4 text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {learnedVendors.map((vendor, idx) => (
                      <tr key={idx} className="border-b" style={{ borderColor: 'var(--color-border-faint)' }}>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" style={{ color: 'var(--color-verified)' }} />
                            <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{vendor.vendor_name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="px-2.5 py-1 rounded-full text-xs font-medium" style={getCategoryColor(vendor.category)}>
                            {vendor.category}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-sm" style={{ color: 'var(--color-text-secondary)' }}>{vendor.subcategory || '-'}</td>
                        <td className="py-3 px-4 text-sm" style={{ color: 'var(--color-text-secondary)' }}>{vendor.approved_by}</td>
                        <td className="py-3 px-4 text-sm" style={{ color: 'var(--color-text-muted)' }}>
                          {vendor.approved_at ? new Date(vendor.approved_at).toLocaleDateString() : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Test Extraction Tab */}
      {activeTab === 'test' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 shadow-sm border" style={{ backgroundColor: 'var(--color-bg-surface)', borderColor: 'var(--color-border)' }}>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
              <Zap className="w-5 h-5" style={{ color: 'var(--color-brand)' }} />
              Test Vendor NER Extraction
            </h3>
            <p className="text-sm mb-4" style={{ color: 'var(--color-text-muted)' }}>
              Enter a transaction description to test the HuggingFace NER vendor extraction
            </p>

            <div className="flex gap-3 mb-6">
              <input
                type="text"
                value={testDescription}
                onChange={(e) => setTestDescription(e.target.value)}
                placeholder="e.g., ACH DEBIT TO SERVPRO OF DOWNTOWN"
                className="flex-1 px-4 py-2.5 border rounded-lg"
                style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-primary)' }}
                onKeyDown={(e) => e.key === 'Enter' && testExtraction()}
              />
              <button
                onClick={testExtraction}
                disabled={testing || !testDescription.trim()}
                className="px-6 py-2.5 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
              >
                {testing ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
                Extract
              </button>
            </div>

            {/* Test Result */}
            {testResult && (
              <div className="rounded-xl p-5 border" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}>
                <h4 className="font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>Extraction Result</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Vendor Name</p>
                    <p className="font-medium text-lg" style={{ color: 'var(--color-text-primary)' }}>{testResult.vendor_name || 'Not detected'}</p>
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Confidence</p>
                    <p className="font-medium text-lg" style={{
                      color: testResult.confidence >= 0.85 ? 'var(--color-verified)' :
                      testResult.confidence >= 0.6 ? 'var(--color-brand)' : 'var(--color-needs-human-text)'
                    }}>
                      {(testResult.confidence * 100).toFixed(0)}%
                    </p>
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Source</p>
                    <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>{testResult.source}</p>
                  </div>
                  <div>
                    <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Known Vendor</p>
                    <p className="flex items-center gap-1">
                      {testResult.is_known_vendor ? (
                        <>
                          <CheckCircle className="w-4 h-4" style={{ color: 'var(--color-verified)' }} />
                          <span className="font-medium" style={{ color: 'var(--color-verified)' }}>Yes</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="w-4 h-4" style={{ color: 'var(--color-text-dim)' }} />
                          <span style={{ color: 'var(--color-text-muted)' }}>No</span>
                        </>
                      )}
                    </p>
                  </div>
                  {testResult.vendor_category && (
                    <div className="col-span-2">
                      <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Category</p>
                      <span className="inline-block px-3 py-1 rounded-full text-sm font-medium" style={getCategoryColor(testResult.vendor_category)}>
                        {testResult.vendor_category}
                      </span>
                    </div>
                  )}
                  {testResult.reasoning && (
                    <div className="col-span-2">
                      <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Reasoning</p>
                      <p style={{ color: 'var(--color-text-secondary)' }}>{testResult.reasoning}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Example Descriptions */}
            <div className="mt-6">
              <p className="text-sm mb-2" style={{ color: 'var(--color-text-muted)' }}>Try these examples:</p>
              <div className="flex flex-wrap gap-2">
                {[
                  'ACH DEBIT TO SERVPRO OF DOWNTOWN',
                  'WIRE TRANSFER TO BELFOR RESTORATION',
                  'GUSTO PAYROLL 12/15/2024',
                  'SYSCO FOOD SERVICES INC',
                  'CROWDSTRIKE INCIDENT RESPONSE',
                  'UNKNOWN VENDOR XYZ123'
                ].map((example, idx) => (
                  <button
                    key={idx}
                    onClick={() => setTestDescription(example)}
                    className="px-3 py-1.5 text-sm rounded-lg transition-colors"
                    style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
